import {
    isPaidPlan,
    normalizePlanSlug,
    SubscriptionPlanIds,
    type SubscriptionPlanId,
} from "@/domains/subscription/plans";
import { TRIAL_DAYS } from "@/domains/subscription/subscriptionConstants";

export { TRIAL_DAYS };

export const PLAN_INTENT_STORAGE_KEY = "mivo_plan_intent";
export const PLAN_INTENT_AUTO_OPEN_KEY = "mivo_plan_intent_auto_open_consumed";

const VALID_PLAN_IDS = new Set<string>(Object.values(SubscriptionPlanIds));

const LEGACY_PLAN_ALIASES: Record<string, SubscriptionPlanId> = {
    básico: SubscriptionPlanIds.basic,
    basico: SubscriptionPlanIds.basic,
    basic: SubscriptionPlanIds.basic,
    gratuito: SubscriptionPlanIds.free,
    freemium: SubscriptionPlanIds.free,
    free: SubscriptionPlanIds.free,
    pro: SubscriptionPlanIds.pro,
    professional_30: SubscriptionPlanIds.pro,
    proplus: SubscriptionPlanIds.pro,
    "pro+": SubscriptionPlanIds.pro,
    professional_50: SubscriptionPlanIds.pro,
};

export function parsePlanIntentParam(value: string | null | undefined): SubscriptionPlanId | null {
    if (!value?.trim()) {
        return null;
    }

    if (VALID_PLAN_IDS.has(value)) {
        return value as SubscriptionPlanId;
    }

    return LEGACY_PLAN_ALIASES[value.trim().toLowerCase()] ?? null;
}

export function setPlanIntent(planId: SubscriptionPlanId): void {
    sessionStorage.setItem(PLAN_INTENT_STORAGE_KEY, planId);
}

export function getPlanIntent(): SubscriptionPlanId | null {
    return parsePlanIntentParam(sessionStorage.getItem(PLAN_INTENT_STORAGE_KEY));
}

export function clearPlanIntent(): void {
    sessionStorage.removeItem(PLAN_INTENT_STORAGE_KEY);
    sessionStorage.removeItem(PLAN_INTENT_AUTO_OPEN_KEY);
}

export function buildCadastroPlanUrl(planId: SubscriptionPlanId): string {
    return `/cadastro?plan=${encodeURIComponent(planId)}`;
}

/** Returns true once per paid intent; marks auto-open as consumed. */
export function consumeAutoOpenCheckout(): boolean {
    const intent = getPlanIntent();
    if (!intent || !isPaidPlan(intent)) {
        return false;
    }

    if (sessionStorage.getItem(PLAN_INTENT_AUTO_OPEN_KEY) === "1") {
        return false;
    }

    sessionStorage.setItem(PLAN_INTENT_AUTO_OPEN_KEY, "1");
    return true;
}

export function resolveDisplayPlanId(
    subscriptionPlan: string | null | undefined,
    isTrial: boolean,
    pendingPaidPlanId?: string | null,
): SubscriptionPlanId {
    if (isTrial) {
        if (pendingPaidPlanId && isPaidPlan(pendingPaidPlanId)) {
            return normalizePlanSlug(pendingPaidPlanId);
        }

        return getPlanIntent() ?? normalizePlanSlug(subscriptionPlan);
    }

    return normalizePlanSlug(subscriptionPlan);
}

export function toRegisterPlanApi(
    planId: SubscriptionPlanId | null | undefined,
): "Basico" | "Pro" | "Free" | undefined {
    if (!planId) {
        return undefined;
    }

    if (planId === SubscriptionPlanIds.free) {
        return "Free";
    }

    if (planId === SubscriptionPlanIds.basic) {
        return "Basico";
    }

    return "Pro";
}
